/**
 * Realtime → React Query cache-invalidation bridge.
 *
 * The shell QueryClient runs with a 30-second `staleTime` plus
 * `refetchOnWindowFocus: 'always'` (App.tsx, H-6 freshness defaults), which
 * keeps navigation snappy but still decouples cached reads from realtime
 * updates between focus events: a SignalR event mutates server state, yet a
 * cached `useApiQuery` result stays "fresh" for up to 30 seconds and never
 * refetches while the tab keeps focus. Per-aggregate hooks (`useRealtime.ts`)
 * close this gap only for what they read, and only while a component using
 * them is mounted — a list/detail view fetched via a plain `useApiQuery` sees
 * no live refresh inside that window. This bridge closes the gap the moment
 * the event arrives instead of waiting out the 30 s (or a refocus).
 *
 * This component subscribes to the core hub events through the SHARED
 * `HubConnectionProvider` (via `useSignalREvent`) and invalidates the matching
 * tenant-scoped query keys whenever an event arrives. Invalidation is by
 * resource prefix via {@link invalidateApiQueries}, so every cached filter
 * variant of the affected resource is marked stale and refetched on next use —
 * but only inside the active tenant (the key factory is tenant-first).
 *
 * Why a component (not the old imperative SignalRManager + EventBridge): there
 * is now ONE realtime connection path — the provider's ref-counted sockets,
 * which modules and ui-kit widgets already share. Driving invalidation off the
 * same sockets removes a second, parallel set of connections and unifies
 * token-refresh-triggered reconnect onto the provider's single token effect.
 */

import { useMemo, type FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSignalREvent, invalidateApiQueries } from '@tyndo/hooks';
import { recordEvent } from '../observability/telemetry';

/**
 * Minimal shape of a manifest realtime-subscription descriptor. The shell's
 * `ManifestModuleEntry.realtimeSubscriptions` is typed `unknown[]` (gateway
 * payload), so we narrow defensively here rather than trusting the wire shape.
 */
interface RealtimeSubscriptionDescriptor {
  hubName?: string;
  methodName?: string;
}

/** Core hub names exposed by the gateway (`/hubs/<name>`). */
export type HubName = 'notifications' | 'alarms' | 'production' | 'equipment';

const HUB_NAMES: readonly HubName[] = [
  'notifications',
  'alarms',
  'production',
  'equipment',
];

/**
 * Default server→client method names per hub. These mirror the SignalR hub
 * method names the gateway broadcasts. Module manifests can layer additional
 * methods on top via `realtimeSubscriptions` descriptors.
 */
const DEFAULT_SUBSCRIPTIONS: Record<HubName, string[]> = {
  notifications: ['ReceiveNotification', 'NotificationRead', 'NotificationCleared'],
  alarms: ['AlarmRaised', 'AlarmCleared', 'AlarmAcknowledged', 'AlarmShelved'],
  production: ['ProductionUpdated', 'OrderStateChanged', 'OeeUpdated'],
  equipment: ['EquipmentStateChanged', 'EquipmentModeChanged', 'TelemetryUpdated'],
};

/**
 * Maps a realtime event method name to the resource path prefix(es) whose
 * cached queries should be invalidated when that event fires. Paths are the
 * same endpoint prefixes the reads use, so prefix-matching invalidates all
 * filtered variants of the resource. Keep aligned with DEFAULT_SUBSCRIPTIONS.
 */
const EVENT_RESOURCE_MAP: Record<string, string[]> = {
  // notifications hub
  ReceiveNotification: ['/notifications'],
  NotificationRead: ['/notifications'],
  NotificationCleared: ['/notifications'],
  // alarms hub
  AlarmRaised: ['/alarms'],
  AlarmCleared: ['/alarms'],
  AlarmAcknowledged: ['/alarms'],
  AlarmShelved: ['/alarms'],
  // production hub
  ProductionUpdated: ['/production'],
  OrderStateChanged: ['/production', '/orders', '/jobs'],
  OeeUpdated: ['/analytics', '/production'],
  // equipment hub
  EquipmentStateChanged: ['/equipment'],
  EquipmentModeChanged: ['/equipment'],
  TelemetryUpdated: ['/equipment', '/historian'],
};

function isHubName(value: string): value is HubName {
  return (HUB_NAMES as readonly string[]).includes(value);
}

/**
 * Build the de-duplicated per-hub method list from the built-in defaults plus
 * any manifest-declared subscription descriptors.
 */
function resolveHubMethods(
  manifestSubscriptions: readonly unknown[],
): Array<{ hub: HubName; method: string }> {
  const byHub = new Map<HubName, Set<string>>();
  for (const hub of HUB_NAMES) {
    byHub.set(hub, new Set(DEFAULT_SUBSCRIPTIONS[hub]));
  }
  for (const raw of manifestSubscriptions) {
    const sub = raw as RealtimeSubscriptionDescriptor;
    if (sub?.hubName && sub?.methodName && isHubName(sub.hubName)) {
      byHub.get(sub.hubName)!.add(sub.methodName);
    }
  }
  const pairs: Array<{ hub: HubName; method: string }> = [];
  for (const hub of HUB_NAMES) {
    for (const method of byHub.get(hub)!) {
      pairs.push({ hub, method });
    }
  }
  return pairs;
}

interface EventInvalidatorProps {
  hub: HubName;
  method: string;
  tenantIdRef: { current: string | null };
}

/**
 * One subscription to a single (hub, method) on the shared provider socket.
 * On each event it invalidates the resource prefixes mapped to that method,
 * scoped to the currently-active tenant (read lazily from the ref).
 */
const EventInvalidator: FC<EventInvalidatorProps> = ({
  hub,
  method,
  tenantIdRef,
}) => {
  const queryClient = useQueryClient();
  useSignalREvent(hub, method, () => {
    const resources = EVENT_RESOURCE_MAP[method];
    if (!resources) return;
    const tenantId = tenantIdRef.current;
    void invalidateApiQueries(queryClient, tenantId, resources).catch(
      (error) => {
        recordEvent('realtime.invalidate_failed', 'warn', {
          eventName: method,
          error: error instanceof Error ? error.message : String(error),
        });
      },
    );
  });
  return null;
};

export interface RealtimeInvalidationBridgeProps {
  /**
   * Realtime subscription descriptors collected from the UI manifest. Typed
   * loosely (`unknown[]`) to match the shell's `ManifestModuleEntry` payload;
   * each entry is narrowed defensively before use.
   */
  manifestSubscriptions: readonly unknown[];
  /** Active tenant, read lazily on each event so invalidation stays scoped. */
  tenantIdRef: { current: string | null };
}

/**
 * Mount inside the {@link HubConnectionProvider} tree (and only while authed
 * with a loaded manifest) to bridge core hub events to React Query cache
 * invalidation. Subscriptions follow the shared sockets' lifecycle, so a
 * token-refresh reconnect or a hub reconnect re-attaches automatically with no
 * separate connection bookkeeping.
 */
export const RealtimeInvalidationBridge: FC<
  RealtimeInvalidationBridgeProps
> = ({ manifestSubscriptions, tenantIdRef }) => {
  // Only re-derive the (hub, method) pairs when the manifest descriptors
  // change; the array drives a stable set of subscription elements.
  const pairs = useMemo(
    () => resolveHubMethods(manifestSubscriptions),
    [manifestSubscriptions],
  );
  return (
    <>
      {pairs.map(({ hub, method }) => (
        <EventInvalidator
          key={`${hub}:${method}`}
          hub={hub}
          method={method}
          tenantIdRef={tenantIdRef}
        />
      ))}
    </>
  );
};
